import { createContext, useCallback, useMemo } from "react";
import axios from "../../utils/axios";
// settings
import actionTypes from "../../common/actionTypes";
import { DATABASE_STATUTES } from "../../common/constants";
// hooks
import useCustomReducer from "../../hooks/useCustomReducer";
// reducers
import { reportsReducer } from "../../reducers/V2/reportsReducer";
// utils
import shopifyAdminFetch from "../../utils/shopifyAdminFetch";

// Endpoints
const PREORDER_REVENUE_ENDPOINT = "/v2/reports/preorder/revenue";
const TOP_PREORDER_PRODUCTS_ENDPOINT = "/v2/reports/preorder/topProducts";
const PREORDER_NOTIFICATIONS_TRENDS_ENDPOINT = "/v2/reports/preorder/notifications";
const BACK_IN_STOCK_TRENDS_ENDPOINT = "/v2/reports/backInStock/trends";
const BACK_IN_STOCK_CONVERSIONS_ENDPOINT = "/v2/reports/backInStock/conversions";
const TOP_BACK_IN_STOCK_PRODUCTS_ENDPOINT = "/v2/reports/backInStock/topProducts";
const BACK_IN_STOCK_NOTIFICATIONS_ENDPOINT = "/v2/notifications/back-in-stock";
const EMAIL_LOGS_ENDPOINT = "/v2/notifications/email-logs";
const ORDERS_ENDPOINT = "/v2/orders";
const SEND_SHIPPING_DELAY_EMAILS_ENDPOINT = "/v2/notifications/orders/shipping-delay/send";

const BACK_IN_STOCK_NOTIFICATIONS_LIMIT = 50;
const EMAIL_LOGS_LIMIT = 50;
const ORDERS_LIMIT = 50;

const fetchOrderData = (id) => `
{
  order(id: "gid://shopify/Order/${id}") {
    name
    displayFulfillmentStatus
    displayFinancialStatus
    email
    tags
    customer {
      displayName
    }
    shippingAddress {
      address1
      address2
      city
      province
      zip
      country
      countryCodeV2
      firstName
      lastName
      name
      phone
    }
    lineItems(first:100) {
      edges {
        node {
          id
          title
          variantTitle
          quantity
          image {
              url
          }
          originalUnitPriceSet {
            shopMoney {
              amount
              currencyCode
            }
          }
          originalTotalSet {
            shopMoney {
              amount
              currencyCode
            }
          }
          sellingPlan {
            name
            sellingPlanId
          }
        }
      }
    }
    totalShippingPriceSet {
      shopMoney {
        amount
        currencyCode
      }
    }
    totalDiscountsSet {
      shopMoney {
        amount
        currencyCode
      }
    }
    totalPriceSet {
      shopMoney {
        amount
        currencyCode
      }
    }
    paymentTerms {
      id
      paymentSchedules(first:1) {
        edges {
          node {
            id
            dueAt
            amount {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
}
`;

const initialState = {
  preorderRevenue: {
    dataPoints: [],
    periodUnit: "DAY",
  },
  topPreorderProducts: {
    products: [],
  },
  preorderNotificationsTrends: {
    confirmation: {
      dataPoints: [],
      periodUnit: "DAY",
    },
    shippingDelay: {
      dataPoints: [],
      periodUnit: "DAY",
    },
  },
  backInStockTrends: {
    dataPoints: [],
    periodUnit: "DAY",
  },
  backInStockConversions: {
    dataPoints: [],
    periodUnit: "DAY",
  },
  topBackInStockProducts: {
    products: [],
  },
  backInStockNotifications: {
    items: [],
    pagination: {
      page: 1,
      limit: BACK_IN_STOCK_NOTIFICATIONS_LIMIT,
      total: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
    selectedIds: new Set(),
  },
  emailLogs: {
    items: [],
    pagination: {
      page: 1,
      limit: EMAIL_LOGS_LIMIT,
      total: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
  },
  orders: {
    items: [],
    pagination: {
      page: 1,
      limit: ORDERS_LIMIT,
      total: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
    selectedIds: new Set(),
  },
  dateRange: {
    start: null,
    end: null,
  },
  statuses: {
    loadPreorderRevenue: DATABASE_STATUTES.NOT_LOADED,
    loadTopPreorderProducts: DATABASE_STATUTES.NOT_LOADED,
    loadPreorderNotificationsTrends: DATABASE_STATUTES.NOT_LOADED,
    loadBackInStockTrends: DATABASE_STATUTES.NOT_LOADED,
    loadBackInStockConversions: DATABASE_STATUTES.NOT_LOADED,
    loadTopBackInStockProducts: DATABASE_STATUTES.NOT_LOADED,
    loadBackInStockNotifications: DATABASE_STATUTES.NOT_LOADED,
    deleteBackInStockNotification: DATABASE_STATUTES.NOT_LOADED,
    loadEmailLogs: DATABASE_STATUTES.NOT_LOADED,
    loadOrders: DATABASE_STATUTES.NOT_LOADED,
    sendShippingDelayEmails: DATABASE_STATUTES.NOT_LOADED,
  },
  errors: {
    preorderRevenue: null,
    topPreorderProducts: null,
    preorderNotificationsTrends: null,
    backInStockTrends: null,
    backInStockConversions: null,
    topBackInStockProducts: null,
    backInStockNotifications: null,
    deleteBackInStockNotification: null,
    emailLogs: null,
    orders: null,
    sendShippingDelayEmails: null,
  },
};

const ReportsContext = createContext({
  ...initialState,
  fetchPreorderRevenue: () => Promise.resolve(),
  fetchTopPreorderProducts: () => Promise.resolve(),
  fetchPreorderNotificationsTrends: () => Promise.resolve(),
  fetchBackInStockTrends: () => Promise.resolve(),
  fetchBackInStockConversions: () => Promise.resolve(),
  fetchTopBackInStockProducts: () => Promise.resolve(),
  fetchBackInStockNotifications: () => Promise.resolve(),
  deleteBackInStockNotification: () => Promise.resolve(),
  fetchEmailLogs: () => Promise.resolve(),
  fetchOrders: () => Promise.resolve(),
  fetchOrder: () => Promise.resolve(),
  fetchOrderFromShopify: () => Promise.resolve(),
  sendShippingDelayEmails: () => Promise.resolve(),
  updateDateRange: () => {},
  getDefaultDateRange: () => ({}),
  setBackInStockSelections: () => {},
  clearBackInStockSelections: () => {},
  setOrdersSelections: () => {},
  clearOrdersSelections: () => {},
});

const ReportsProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(reportsReducer, initialState);

  const getDefaultDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6); // 7 days including today

    return { start, end };
  }, []);

  const updateDateRange = useCallback(
    (dateRange) => {
      dispatch({
        type: actionTypes.UPDATE_REPORTS_DATE_RANGE,
        payload: dateRange,
      });
    },
    [dispatch]
  );

  const fetchPreorderRevenue = useCallback(
    async (dateRange = null) => {
      try {
        const range = dateRange || state.dateRange || getDefaultDateRange();

        dispatch({ type: actionTypes.FETCH_PREORDER_REVENUE });

        const response = await axios.get(PREORDER_REVENUE_ENDPOINT, {
          params: {
            fromDate: range.start.toISOString(),
            toDate: range.end.toISOString(),
          },
        });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_PREORDER_REVENUE_SUCCESS,
            payload: {
              data: response.data.data,
              dateRange: range,
            },
          });
          return response.data.data;
        } else {
          throw new Error(response.data?.message || "Failed to fetch preorder revenue");
        }
      } catch (error) {
        console.error("Error fetching preorder revenue:", error);
        dispatch({
          type: actionTypes.FETCH_PREORDER_REVENUE_FAILURE,
          error: error.message || "Failed to fetch preorder revenue",
        });
        throw error;
      }
    },
    [dispatch, getDefaultDateRange]
  );

  const fetchTopPreorderProducts = useCallback(
    async (dateRange = null, limit = 15) => {
      try {
        const range = dateRange || state.dateRange || getDefaultDateRange();

        dispatch({ type: actionTypes.FETCH_TOP_PREORDER_PRODUCTS });

        const response = await axios.get(TOP_PREORDER_PRODUCTS_ENDPOINT, {
          params: {
            fromDate: range.start.toISOString(),
            toDate: range.end.toISOString(),
            limit,
          },
        });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_TOP_PREORDER_PRODUCTS_SUCCESS,
            payload: {
              data: response.data.data,
              dateRange: range,
            },
          });
          return response.data.data;
        } else {
          throw new Error(response.data?.message || "Failed to fetch top preorder products");
        }
      } catch (error) {
        console.error("Error fetching top preorder products:", error);
        dispatch({
          type: actionTypes.FETCH_TOP_PREORDER_PRODUCTS_FAILURE,
          error: error.message || "Failed to fetch top preorder products",
        });
        throw error;
      }
    },
    [dispatch, getDefaultDateRange]
  );

  const fetchPreorderNotificationsTrends = useCallback(
    async (dateRange = null) => {
      try {
        const range = dateRange || state.dateRange || getDefaultDateRange();

        dispatch({ type: actionTypes.FETCH_PREORDER_NOTIFICATIONS_TRENDS });

        // Fetch both CONFIRMATION and SHIPPING_DELAY trends in parallel
        const [confirmationResponse, shippingDelayResponse] = await Promise.all([
          axios.get(PREORDER_NOTIFICATIONS_TRENDS_ENDPOINT, {
            params: {
              fromDate: range.start.toISOString(),
              toDate: range.end.toISOString(),
              emailType: "CONFIRMATION",
            },
          }),
          axios.get(PREORDER_NOTIFICATIONS_TRENDS_ENDPOINT, {
            params: {
              fromDate: range.start.toISOString(),
              toDate: range.end.toISOString(),
              emailType: "SHIPPING_DELAY",
            },
          }),
        ]);

        if (confirmationResponse.data?.success && shippingDelayResponse.data?.success) {
          const data = {
            confirmation: confirmationResponse.data.data,
            shippingDelay: shippingDelayResponse.data.data,
          };

          dispatch({
            type: actionTypes.FETCH_PREORDER_NOTIFICATIONS_TRENDS_SUCCESS,
            payload: {
              data,
              dateRange: range,
            },
          });
          return data;
        } else {
          throw new Error("Failed to fetch preorder notifications trends");
        }
      } catch (error) {
        console.error("Error fetching preorder notifications trends:", error);
        dispatch({
          type: actionTypes.FETCH_PREORDER_NOTIFICATIONS_TRENDS_FAILURE,
          error: error.message || "Failed to fetch preorder notifications trends",
        });
        throw error;
      }
    },
    [dispatch, getDefaultDateRange]
  );

  const fetchBackInStockTrends = useCallback(
    async (dateRange = null) => {
      try {
        const range = dateRange || state.dateRange || getDefaultDateRange();

        dispatch({ type: actionTypes.FETCH_BACK_IN_STOCK_TRENDS });

        const response = await axios.get(BACK_IN_STOCK_TRENDS_ENDPOINT, {
          params: {
            fromDate: range.start.toISOString(),
            toDate: range.end.toISOString(),
          },
        });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_BACK_IN_STOCK_TRENDS_SUCCESS,
            payload: {
              data: response.data.data,
              dateRange: range,
            },
          });
          return response.data.data;
        } else {
          throw new Error(response.data?.message || "Failed to fetch back-in-stock trends");
        }
      } catch (error) {
        console.error("Error fetching back-in-stock trends:", error);
        dispatch({
          type: actionTypes.FETCH_BACK_IN_STOCK_TRENDS_FAILURE,
          error: error.message || "Failed to fetch back-in-stock trends",
        });
        throw error;
      }
    },
    [dispatch, getDefaultDateRange]
  );

  const fetchBackInStockConversions = useCallback(
    async (dateRange = null) => {
      try {
        const range = dateRange || state.dateRange || getDefaultDateRange();

        dispatch({ type: actionTypes.FETCH_BACK_IN_STOCK_CONVERSIONS });

        const response = await axios.get(BACK_IN_STOCK_CONVERSIONS_ENDPOINT, {
          params: {
            fromDate: range.start.toISOString(),
            toDate: range.end.toISOString(),
          },
        });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_BACK_IN_STOCK_CONVERSIONS_SUCCESS,
            payload: {
              data: response.data.data,
              dateRange: range,
            },
          });
          return response.data.data;
        } else {
          throw new Error(response.data?.message || "Failed to fetch back-in-stock conversions");
        }
      } catch (error) {
        console.error("Error fetching back-in-stock conversions:", error);
        dispatch({
          type: actionTypes.FETCH_BACK_IN_STOCK_CONVERSIONS_FAILURE,
          error: error.message || "Failed to fetch back-in-stock conversions",
        });
        throw error;
      }
    },
    [dispatch, getDefaultDateRange]
  );

  const fetchTopBackInStockProducts = useCallback(
    async (dateRange = null, limit = 15) => {
      try {
        const range = dateRange || state.dateRange || getDefaultDateRange();

        dispatch({ type: actionTypes.FETCH_TOP_BACK_IN_STOCK_PRODUCTS });

        const response = await axios.get(TOP_BACK_IN_STOCK_PRODUCTS_ENDPOINT, {
          params: {
            fromDate: range.start.toISOString(),
            toDate: range.end.toISOString(),
            limit,
          },
        });

        if (response.data && response.data.success) {
          // Transform data to match chart expectations (productTitle, totalQuantity)
          const transformedData = {
            products: (response.data.data?.products || []).map((product) => ({
              productTitle: product.productName,
              totalQuantity: product.totalSubscribers,
            })),
          };

          dispatch({
            type: actionTypes.FETCH_TOP_BACK_IN_STOCK_PRODUCTS_SUCCESS,
            payload: {
              data: transformedData,
              dateRange: range,
            },
          });
          return transformedData;
        } else {
          throw new Error(response.data?.message || "Failed to fetch top back-in-stock products");
        }
      } catch (error) {
        console.error("Error fetching top back-in-stock products:", error);
        dispatch({
          type: actionTypes.FETCH_TOP_BACK_IN_STOCK_PRODUCTS_FAILURE,
          error: error.message || "Failed to fetch top back-in-stock products",
        });
        throw error;
      }
    },
    [dispatch, getDefaultDateRange]
  );

  const fetchBackInStockNotifications = useCallback(
    async ({
      page = 1,
      limit = BACK_IN_STOCK_NOTIFICATIONS_LIMIT,
      search,
      sortBy,
      sortOrder,
      ...otherFilters
    } = {}) => {
      dispatch({ type: actionTypes.FETCH_BACK_IN_STOCK_NOTIFICATIONS });

      try {
        const params = {
          page,
          limit,
          ...(search ? { search } : {}),
          ...(sortBy ? { sortBy } : {}),
          ...(sortOrder ? { sortOrder } : {}),
          ...otherFilters,
        };

        const response = await axios.get(BACK_IN_STOCK_NOTIFICATIONS_ENDPOINT, { params });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_BACK_IN_STOCK_NOTIFICATIONS_SUCCESS,
            payload: {
              items: response.data.data?.backInStockNotifications || [],
              pagination: response.data.pagination || {},
            },
          });

          return {
            items: response.data.data?.backInStockNotifications || [],
            pagination: response.data.pagination || {},
          };
        }

        throw new Error(response.data?.message || "Failed to fetch back in stock notifications");
      } catch (error) {
        console.error("Error fetching back in stock notifications:", error);
        dispatch({
          type: actionTypes.FETCH_BACK_IN_STOCK_NOTIFICATIONS_FAILURE,
          error: error.message || "Failed to fetch back in stock notifications",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const deleteBackInStockNotification = useCallback(
    async (notificationId) => {
      try {
        dispatch({
          type: actionTypes.DELETE_BACK_IN_STOCK_NOTIFICATION,
          payload: { id: notificationId },
        });

        await axios.delete(`${BACK_IN_STOCK_NOTIFICATIONS_ENDPOINT}/${notificationId}`);

        dispatch({
          type: actionTypes.DELETE_BACK_IN_STOCK_NOTIFICATION_SUCCESS,
          payload: { id: notificationId },
        });
      } catch (error) {
        console.error("Error deleting back in stock notification:", error);
        dispatch({
          type: actionTypes.DELETE_BACK_IN_STOCK_NOTIFICATION_FAILURE,
          error: error.message || "Failed to delete back in stock notification",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const setBackInStockSelections = useCallback(
    (selectedIds) => {
      dispatch({
        type: actionTypes.SET_BACK_IN_STOCK_SELECTIONS,
        payload: selectedIds,
      });
    },
    [dispatch]
  );

  const clearBackInStockSelections = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_BACK_IN_STOCK_SELECTIONS });
  }, [dispatch]);

  const fetchEmailLogs = useCallback(
    async ({
      page = 1,
      limit = EMAIL_LOGS_LIMIT,
      search,
      sortBy,
      sortOrder,
      ...otherFilters
    } = {}) => {
      dispatch({ type: actionTypes.FETCH_EMAIL_LOGS });

      try {
        const params = {
          page,
          limit,
          ...(search ? { search } : {}),
          ...(sortBy ? { sortBy } : {}),
          ...(sortOrder ? { sortOrder } : {}),
          ...otherFilters,
        };

        const response = await axios.get(EMAIL_LOGS_ENDPOINT, { params });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_EMAIL_LOGS_SUCCESS,
            payload: {
              items: response.data.data?.emailLogs || [],
              pagination: response.data.pagination || {},
            },
          });

          return {
            items: response.data.data?.emailLogs || [],
            pagination: response.data.pagination || {},
          };
        }

        throw new Error(response.data?.message || "Failed to fetch email logs");
      } catch (error) {
        console.error("Error fetching email logs:", error);
        dispatch({
          type: actionTypes.FETCH_EMAIL_LOGS_FAILURE,
          error: error.message || "Failed to fetch email logs",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const fetchOrders = useCallback(
    async ({
      page = 1,
      limit = ORDERS_LIMIT,
      search,
      sortBy,
      sortOrder,
      financialStatus,
      fulfillmentStatus,
      tags,
      fromDate,
      toDate,
    } = {}) => {
      dispatch({ type: actionTypes.FETCH_ORDERS });

      try {
        const params = {
          page: String(page),
          limit: String(limit),
          ...(search ? { search } : {}),
          ...(sortBy ? { sortBy } : {}),
          ...(sortOrder ? { sortOrder } : {}),
          // Map frontend filter names to backend parameter names (arrays will be sent as repeated params)
          ...(financialStatus && financialStatus.length > 0 ? { financialStatuses: financialStatus } : {}),
          ...(fulfillmentStatus && fulfillmentStatus.length > 0 ? { fulfillmentStatuses: fulfillmentStatus } : {}),
          ...(tags && (Array.isArray(tags) ? tags.length > 0 : tags) ? { tags } : {}),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
        };

        // Use URLSearchParams to serialize arrays as repeated query parameters (financialStatuses=X&financialStatuses=Y)
        const paramsSerializer = (params) => {
          const searchParams = new URLSearchParams();
          Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach((item) => searchParams.append(key, String(item)));
            } else {
              searchParams.append(key, String(value));
            }
          });
          return searchParams.toString();
        };

        const response = await axios.get(ORDERS_ENDPOINT, { params, paramsSerializer });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_ORDERS_SUCCESS,
            payload: {
              items: response.data.data?.orders || [],
              pagination: response.data.pagination || {},
            },
          });

          return {
            items: response.data.data?.orders || [],
            pagination: response.data.pagination || {},
          };
        }

        throw new Error(response.data?.message || "Failed to fetch orders");
      } catch (error) {
        console.error("Error fetching orders:", error);
        dispatch({
          type: actionTypes.FETCH_ORDERS_FAILURE,
          error: error.message || "Failed to fetch orders",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const setOrdersSelections = useCallback(
    (selectedIds) => {
      dispatch({
        type: actionTypes.SET_ORDERS_SELECTIONS,
        payload: selectedIds,
      });
    },
    [dispatch]
  );

  const clearOrdersSelections = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_ORDERS_SELECTIONS });
  }, [dispatch]);

  const fetchOrder = useCallback(async (orderId) => {
    try {
      const response = await axios.get(`${ORDERS_ENDPOINT}/${orderId}`);

      if (response.data && response.data.success) {
        return response.data.data?.order || null;
      }

      throw new Error(response.data?.message || "Failed to fetch order");
    } catch (error) {
      console.error("Error fetching order:", error);
      throw error;
    }
  }, []);

  const fetchOrderFromShopify = useCallback(async (shopifyOrderId) => {
    try {
      const { data } = await shopifyAdminFetch({ query: fetchOrderData(shopifyOrderId) });
      return data?.order || null;
    } catch (error) {
      console.error("Error fetching order from Shopify:", error);
      throw error;
    }
  }, []);

  const sendShippingDelayEmails = useCallback(
    async (orderIds, shippingDate, productTitle) => {
      try {
        dispatch({ type: actionTypes.SEND_SHIPPING_DELAY_EMAILS });

        const response = await axios.post(SEND_SHIPPING_DELAY_EMAILS_ENDPOINT, {
          orderIds,
          shippingDate,
          productTitle,
        });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.SEND_SHIPPING_DELAY_EMAILS_SUCCESS,
          });
          return { success: true };
        }

        throw new Error(response.data?.message || "Failed to send shipping delay emails");
      } catch (error) {
        console.error("Error sending shipping delay emails:", error);
        dispatch({
          type: actionTypes.SEND_SHIPPING_DELAY_EMAILS_FAILURE,
          error: error.response?.data?.message || error.message || "Failed to send shipping delay emails",
        });
        throw error;
      }
    },
    [dispatch]
  );

  const memoizedState = useMemo(
    () => ({
      ...state,
      fetchPreorderRevenue,
      fetchTopPreorderProducts,
      fetchPreorderNotificationsTrends,
      fetchBackInStockTrends,
      fetchBackInStockConversions,
      fetchTopBackInStockProducts,
      fetchBackInStockNotifications,
      deleteBackInStockNotification,
      fetchEmailLogs,
      fetchOrders,
      fetchOrder,
      fetchOrderFromShopify,
      sendShippingDelayEmails,
      updateDateRange,
      getDefaultDateRange,
      setBackInStockSelections,
      clearBackInStockSelections,
      setOrdersSelections,
      clearOrdersSelections,
    }),
    [
      state,
      fetchPreorderRevenue,
      fetchTopPreorderProducts,
      fetchPreorderNotificationsTrends,
      fetchBackInStockTrends,
      fetchBackInStockConversions,
      fetchTopBackInStockProducts,
      fetchBackInStockNotifications,
      deleteBackInStockNotification,
      fetchEmailLogs,
      fetchOrders,
      fetchOrder,
      fetchOrderFromShopify,
      sendShippingDelayEmails,
      updateDateRange,
      getDefaultDateRange,
      setBackInStockSelections,
      clearBackInStockSelections,
      setOrdersSelections,
      clearOrdersSelections,
    ]
  );

  return <ReportsContext.Provider value={memoizedState}>{children}</ReportsContext.Provider>;
};

export { ReportsContext, ReportsProvider };
